import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GO_API, useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import { useTenantContext } from '../components/App/AppTenant';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import { AdditionalPaymentTypeModel } from '../defs/defs';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import { EqxDisplayUomInput } from '../shared/components/EqxDisplayUom/EqxDisplayUom';
import EqxError from '../shared/components/EqxError';
import EqxForm, {
    EqxFormInputDate,
    EqxFormInputSelect,
    EqxFormInputTextarea,
    EqxFormRow,
    EqxFormSubmit,
    useFormState
} from '../shared/components/EqxForm/EqxForm';
import EqxIcon from '../shared/components/EqxIcon';
import { default as EqxLoading, default as Loading } from '../shared/components/EqxLoading';
import EqxRequestHandler from '../shared/components/EqxRequestHandler/EqxRequestHandler';
import EqxUploadPanel from '../shared/components/EqxUploadPanel/EqxUploadPanel';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';

// Main content component extracted from PageAdditionalPaymentsCreate
const AdditionalPaymentsCreateContent: React.FC = () => {
    const api = useApi();
    const navigate = useNavigate();
    const tenant = useTenantContext();
    const additionalPaymentTypesRequest = useApiEndpoint<AdditionalPaymentTypeModel[]>(api, api.endpoints.additionalPaymentTypes());
    const { isOffline } = useOffline()

    const formState = useFormState(undefined, {
        data: {
            additionalPaymentTypeID: '',
            date: new Date().toISOString().split('T')[0], // Today's date as default
            quantity: '',
            tax: '',
            notes: '',
            receiptContent: '',
            receiptMimeType: '',
            receiptFileName: ''
        }
    });

    // Check allowed
    const mainContext = useMainLayoutContext()
    if (!mainContext.permissions?.canAddExpenses) {
        return <EqxError error="You do not have permission to create expenses." />
    }
    if (!tenant.isManagedPayroll) {
        return <EqxError error="Managed Payroll is not enabled for this business" />;
    }
    if (!mainContext.user?.staffID) {
        if (!mainContext.user) {
            return <EqxLoading />
        } else {
            return <EqxError error="Only staff can request expenses" />;
        }
    }

    const handleFormSuccess = (response: any) => {
        navigate('/expenses');
    };

    const handleInputFiles = async (files: File[]) => {
        if (files.length > 0) {
            const file = files[0]; // Since singleFile is true, we only get one file

            // Read the file content as base64
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result as string;
                // Remove the data URL prefix to get just the base64 content
                const base64Content = content.split(',')[1];

                // Update form data with file metadata
                formState.setValue('receiptContent', base64Content);
                formState.setValue('receiptMimeType', file.type);
                formState.setValue('receiptFileName', file.name);
            };
            reader.readAsDataURL(file);
        } else {
            formState.setValue('receiptContent', undefined);
            formState.setValue('receiptMimeType', undefined);
            formState.setValue('receiptFileName', undefined);
        }
    };

    const validateForm = (data: any) => {
        const errors: any = {};

        if (!data.additionalPaymentTypeID) {
            errors.additionalPaymentTypeID = 'Payment type is required';
        }

        if (!data.date) {
            errors.date = 'Date is required';
        }

        if (!data.quantity || data.quantity <= 0) {
            errors.quantity = 'Amount must be greater than 0';
        }

        if (data.tax && data.tax < 0) {
            errors.tax = 'Tax cannot be negative';
        }

        return errors;
    };

    return (
        <EqxCard isSection={true} disabled={isOffline} disabledMessage={<><EqxIcon fa="fas fa-wifi " /> Unavailable Offline</>}>
            <EqxForm
                api={api}
                state={formState}
                action={`${GO_API}/additional-payments/create`}
                onSuccess={handleFormSuccess}
                validate={validateForm}
                method="post"
                prepareData={(data) => {
                    // Transform receipt fields into a single receipt object
                    const { receiptContent, receiptMimeType, receiptFileName, ...restData } = data;
                    const hasReceipt = [receiptContent, receiptMimeType, receiptFileName]
                        .some(v => v !== undefined && v !== null && v !== '');

                    return hasReceipt
                        ? {
                            ...restData,
                            receipt: {
                                fileContent: receiptContent,
                                contentType: receiptMimeType,
                                fileName: receiptFileName
                            }
                        }
                        : {
                            ...restData
                        };
                }}
            >
                {/* Hidden inputs for receipt metadata */}
                <input type="hidden" name="receiptContent" />
                <input type="hidden" name="receiptMimeType" />
                <input type="hidden" name="receiptFileName" />

                <EqxFormRow name="additionalPaymentTypeID" label="Payment Type">
                    <EqxRequestHandler
                        state={{ loading: additionalPaymentTypesRequest.loading, error: additionalPaymentTypesRequest.error }}
                        loading={<Loading size="sm" />}
                        error={<span className="text-danger">Error loading payment types</span>}
                        content={() => (
                            <>
                                <EqxFormInputSelect
                                    name="additionalPaymentTypeID"
                                    options={additionalPaymentTypesRequest.response?.map(t => ({ value: t.id.toString(), label: t.name })) || []}
                                    required={true}
                                />
                            </>
                        )} />
                </EqxFormRow>

                <EqxFormRow name="quantity" label="Amount">
                    <EqxDisplayUomInput
                        uom={formState.data.additionalPaymentTypeID &&
                            additionalPaymentTypesRequest.response?.find(t => t.id.toString() === formState.data.additionalPaymentTypeID)?.uom?.name || ''}
                        currencySymbol={formState.data.additionalPaymentTypeID &&
                            additionalPaymentTypesRequest.response?.find(t => t.id.toString() === formState.data.additionalPaymentTypeID)?.uom?.currencySymbol || ''}
                    >
                        <input
                            type="number"
                            className="form-control"
                            value={formState.data.quantity}
                            onChange={(e) => formState.setValue('quantity', e.target.value)}
                            placeholder="Enter Amount"
                            required={true}
                            step={0.01}
                        />
                    </EqxDisplayUomInput>
                </EqxFormRow>

                <EqxFormRow name="date" label="Date">
                    <EqxFormInputDate name="date" required={true} />
                </EqxFormRow>

                <EqxFormRow name="receipt" label="Receipt">
                    <EqxUploadPanel
                        singleFile={true}
                        onInputFiles={handleInputFiles}
                        accept={['image/*', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}
                    />
                    <div className="form-help-text">Upload a receipt or supporting document. Not required for mileage claims.</div>
                </EqxFormRow>

                {formState.data.receiptContent &&
                    <EqxFormRow name="tax" label="Tax Amount">
                        <EqxDisplayUomInput uom="GBP" currencySymbol="£">
                            <input
                                type="number"
                                className="form-control"
                                value={formState.data.tax}
                                onChange={(e) => formState.setValue('tax', e.target.value)}
                                placeholder="Enter tax amount"
                                min={0}
                                step={0.01}
                            />
                        </EqxDisplayUomInput>
                        <div className="form-help-text">Enter the tax/VAT amount shown on the receipt (included in the total)</div>
                    </EqxFormRow>
                }

                <EqxFormRow name="notes" label="Notes">
                    <EqxFormInputTextarea name="notes" placeholder="Enter any additional notes or details..." />
                </EqxFormRow>

                <div className="d-flex justify-content-end mt-3">
                    <EqxFormSubmit label="Create Expense" variant="success" className='w-100' />
                </div>
            </EqxForm>
        </EqxCard >
    );
};

const PageAdditionalPaymentsCreate: React.FC = () => {
    return (
        <MainLayout pageTitle="Create Expense" relatedLinks={[{ text: 'Expenses', url: '/expenses' }]}>
            <AdditionalPaymentsCreateContent />
        </MainLayout>
    );
};

export default PageAdditionalPaymentsCreate; 